import React, { lazy, Suspense, useCallback } from 'react'
import { HashRouter as Router, Route, Switch } from 'react-router-dom'
import PublicRoute from './routes/PublicRoute'
import CircleLoading from './components/public/loading/CircleLoading'
import PrivateRoute from './routes/PrivateRoute'
import { ReactQueryDevtools } from 'react-query-devtools'
import { CssBaseline } from '@material-ui/core'
import routes from './routes'
import DashboardContent from './components/screen/dashboard/DashboardContent'
import { errorHandler, isAdmin, JwtData, tWarn } from './utils'
import Appbar from './components/screen/dashboard/AppBar'
import ReactGA from 'react-ga'
import { createBrowserHistory } from 'history'
import { ToastContainer } from 'react-toastify'
import { Subscription } from 'services/api'
import { GetValuesOfEnum, PharmacyRoleEnum, RolesEnum } from 'enum'
import SubscriptionUser from 'services/api/SubscriptionUser'

const Login = lazy(() => import('./components/screen/login/Login'))
const LoginNew = lazy(() => import('./components/screen/login/LoginNew'))
const LoginPharmacy = lazy(
  () => import('./components/screen/login/LoginPharmacy'),
)
const ForgetPasswordPharmacy = lazy(
  () => import('./components/screen/forget-password/ForgetPasswordPharmacy'),
)
const ForgetPasswordNew = lazy(
  () => import('./components/screen/forget-password/ForgetPasswordNew'),
)
const OtpPharmacy = lazy(() => import('./components/screen/otp/OtpPharmacy'))
const OtpNew = lazy(() => import('./components/screen/otp/OtpNew'))
const Dashboard = lazy(() => import('./components/screen/dashboard/Dashboard'))
const ForgetPassword = lazy(
  () => import('./components/screen/forget-password/ForgetPassword'),
)
const Otp = lazy(() => import('./components/screen/otp/Otp'))

const OtpSecondStep = lazy(() => import('./components/screen/otp/SecondStep'))

const RegisterPharmacyWithUser = lazy(
  () => import('./components/screen/public/RegisterPharmacyWithUser'),
)

const DrugTransfer = lazy(
  () => import('./components/screen/dashboard/drug-transfer/Transfer'),
)
const Desktop1 = lazy(
  () => import('./components/screen/dashboard/drug-transfer/desktop/Desktop1'),
)
const Transfer = lazy(
  () => import('./components/screen/dashboard/drug-transfer/Transfer'),
)
const Survey = lazy(
  () => import('./components/screen/dashboard/drug-transfer/Survey'),
)
const SupplyList = lazy(
  () => import('./components/screen/dashboard/supply-list/SupplyList'),
)
const SuggestDrug = lazy(
  () => import('./components/screen/dashboard/supply-list/suggest-drug'),
)

const Transactions = lazy(
  () => import('./components/screen/dashboard/accounting/accountingList'),
)

const AccountingInfo = lazy(
  () => import('./components/screen/dashboard/accounting/AccountingInfo'),
)

const AddSubscription = lazy(
  () => import('./components/screen/dashboard/subscription/AddSubscription'),
)

const AddSubscriptionForUser = lazy(
  () =>
    import('./components/screen/dashboard/subscription/AddSubscriptionForUser'),
)

const AddUserSubscription = lazy(
  () =>
    import(
      './components/screen/dashboard/subscriptionUser/AddUserSubscription'
    ),
)

const ManageSubscriptions = lazy(
  () =>
    import('./components/screen/dashboard/subscription/ManageSubscriptions'),
)

const ManageUserSubscriptions = lazy(
  () =>
    import(
      './components/screen/dashboard/subscriptionUser/ManageUserSubscriptions'
    ),
)

const SubscriptionList = lazy(
  () => import('./components/screen/dashboard/subscription/SubscriptionList'),
)

const SubscriptionUserList = lazy(
  () =>
    import(
      './components/screen/dashboard/subscriptionUser/SubscriptionUserList'
    ),
)
const MembershipRequests = lazy(
  () => import('./components/screen/dashboard/pharmacy/Membership'),
)
const MemberRole = lazy(
  () => import('./components/screen/dashboard/pharmacy/MemberRole'),
)

const DashboardActivePage = lazy(
  () => import('./components/screen/dashboard/DashboardActivePage'),
)

const CreateRole = lazy(
  () => import('./components/screen/dashboard/roles/CreateRole'),
)

const CreateUser = lazy(
  () => import('./components/screen/dashboard/user/CreateUser'),
)

const UsersList = lazy(
  () => import('./components/screen/dashboard/user/UsersList'),
)

const ChangeUserPassword = lazy(
  () => import('./components/screen/dashboard/user/ChangePassword'),
)

const CreateDrug = lazy(
  () => import('./components/screen/dashboard/drug/CreateDrug'),
)

const DrugsList = lazy(
  () => import('./components/screen/dashboard/drug/drugsList'),
)

const CategoryList = lazy(
  () => import('./components/screen/dashboard/category/CategoryList'),
)
const FavoriteList = lazy(
  () => import('./components/screen/dashboard/favorite-list/Drug'),
)
const Prescription = lazy(
  () =>
    import(
      './components/screen/dashboard/peopleSection/Prescription/Prescription'
    ),
)
const EmploymentApplication = lazy(
  () =>
    import(
      './components/screen/dashboard/peopleSection/EmploymentApplication/EmploymentApplication'
    ),
)
const FindJob = lazy(
  () => import('./components/screen/dashboard/peopleSection/FindJob/FindJob'),
)
const AddTransaction = lazy(
  () =>
    import(
      './components/screen/dashboard/peopleSection/Transaction/AddTransaction'
    ),
)
const CreatePharmacy = lazy(
  () => import('./components/screen/dashboard/pharmacy/createPharmacy'),
)

const PharmaciesList = lazy(
  () => import('./components/screen/dashboard/pharmacy/pharmaciesList'),
)

const PharmacyDocs = lazy(
  () => import('./components/screen/dashboard/pharmacy/PharmacyDocs'),
)

const CreateMessage = lazy(
  () => import('./components/screen/dashboard/message/CreateMessage'),
)

const MessagesList = lazy(
  () => import('./components/screen/dashboard/message/MessagesList'),
)
const ReviewsList = lazy(
  () => import('./components/screen/dashboard/review/ReviewsList'),
)

const DrugFavoriteCategory = lazy(
  () => import('./components/screen/dashboard/favorite-list/category/Category'),
)

const CreatePack = lazy(
  () => import('./components/screen/dashboard/pack/create/Create'),
)

const PackList = lazy(() => import('./components/screen/dashboard/pack/Pack'))

const ExchangeManagement = lazy(
  () =>
    import(
      './components/screen/dashboard/exchange-management/ExchangeManagement'
    ),
)

const FDA_exchangeList = lazy(
  () => import('./components/screen/dashboard/fda/exchanges'),
)

const PharmacyUsersList = lazy(
  () => import('./components/screen/dashboard/pharmacy/UsersList'),
)

const JobSearchList = lazy(
  () => import('./components/screen/dashboard/job-search/jobSearchList'),
)

const PrescriptionList = lazy(
  () => import('./components/screen/dashboard/prescription/prescriptionList'),
)

const ProfileLazy = lazy(
  () => import('./components/screen/dashboard/user/Profile'),
)

const PharmacyProfileLazy = lazy(
  () => import('./components/screen/dashboard/pharmacy/Profile'),
)

const SettingsForm = lazy(
  () => import('./components/screen/dashboard/settings/Settings'),
)
const SettingsAiForm = lazy(
  () => import('./components/screen/dashboard/settingsAi/SettingsAi'),
)
const FavoriteDrugsForm = lazy(
  () => import('./components/screen/dashboard/reports/FavoriteDrugs'),
)
const AllFavoritesForm = lazy(
  () => import('./components/screen/dashboard/favorite-list/favorites-list'),
)
const SelectedDrugsForm = lazy(
  () => import('./components/screen/dashboard/reports/SelectedDrugs'),
)
const SurplusDrugsForm = lazy(
  () => import('./components/screen/dashboard/reports/SurplusDrugs'),
)
const LoginCountReport = lazy(
  () => import('./components/screen/dashboard/reports/LoginCountReport'),
)
const AllPharmacyDrugReport = lazy(
  () => import('./components/screen/dashboard/reports/AllPharmacyDrugReport'),
)
const AcceptedPrescriptionsReport = lazy(
  () =>
    import('./components/screen/dashboard/reports/AcceptedPrescriptionsReport'),
)
const AllEmploymentApplicationsReport = lazy(
  () =>
    import(
      './components/screen/dashboard/reports/AllEmploymentApplicationsReport'
    ),
)
const AllAccountingReport = lazy(
  () => import('./components/screen/dashboard/reports/AllAccountingReport'),
)
const PharmacySubscriptions = lazy(
  () => import('./components/screen/dashboard/pharmacy/PharmacySubscriptions'),
)

const UserSubscriptions = lazy(
  () => import('./components/screen/dashboard/user/UserSubscriptions'),
)
const UserTransactions = lazy(
  () => import('./components/screen/dashboard/user/UserTransactions'),
)
const AllAccountingFullReport = lazy(
  () => import('./components/screen/dashboard/reports/AllAccountingFullReport'),
)
const AllPharmacyWithSurplusDrugsReport = lazy(
  () =>
    import('./components/screen/dashboard/reports/AllPharmacyWithSurplusDrugs'),
)

const AllSubscriptionReport = lazy(
  () => import('./components/screen/dashboard/reports/AllSubscriptionReport'),
)

const AllSubscriptionUserReport = lazy(
  () =>
    import('./components/screen/dashboard/reports/AllSubscriptionUserReport'),
)

const AllPacksReport = lazy(
  () => import('./components/screen/dashboard/reports/all-packs-report'),
)
const OstanRankingReport = lazy(
  () => import('./components/screen/dashboard/reports/OstanRankingReport'),
)

const About = lazy(() => import('./components/screen/dashboard/about/About'))

const GroupDrugCategory = lazy(
  () => import('./components/screen/dashboard/admin/GroupDrugCategory'),
)

const Job = lazy(() => import('./components/screen/dashboard/job/Job'))

const PharmacyMessage = lazy(
  () => import('./components/screen/dashboard/pharmacy/message/Message'),
)
const SurveyComponent = lazy(
  () => import('./components/screen/dashboard/pharmacy/survey/SurveyComponent'),
)
const APharmacyDocs = lazy(
  () => import('./components/screen/dashboard/pharmacy/docs/docs'),
)
const CommisionsList = lazy(
  () =>
    import('./components/screen/dashboard/settingsCommision/CommisionsList'),
)
const EmploymentApplicationForAdmin = lazy(
  () =>
    import(
      './components/screen/dashboard/employment/admin/EmploymentApplication'
    ),
)

const ParcelList = lazy(
  () =>
    import(
      './components/screen/dashboard/zboomParcel/ParcelList'
    ),
)

const DeclarationsList = lazy(
  () => import('./components/screen/dashboard/declaration/declarationsList'),
)

const PharmacyDrugHistoryReport = lazy(
  () =>
    import('./components/screen/dashboard/reports/PharmacyDrugHistoryReport'),
)

const Error404 = lazy(() => import('./components/screen/public/404'))
const Error401 = lazy(() => import('./components/screen/public/401'))

const {
  login,
  loginNew,
  loginPharmacy,
  forgetPasswordPharmacy,
  forgetPasswordNew,
  otpPharmacy,
  otpNew,
  defaultPath,
  drugFavoriteList,
  prescription,
  jobApplication,
  findJob,
  addTransaction,
  dashboard,
  transfer,
  desktop,
  supplyList,
  suggestDrug,
  transactions,
  membershipRequests,
  registerPharmacyWithUser,
  memberRole,
  createRole,
  createUser,
  usersList,
  changeUserPassword,
  createDrug,
  drugsList,
  categoryList,
  createPharmacy,
  pharmaciesList,
  pharmacyDocs,
  createMessage,
  messagesList,
  reviewsList,
  drugCategoryfavoriteList,
  accountingInfo,
  packsList,
  createPack,
  exchangeManagementList,
  pharmacyUsersList,
  jobSearchList,
  prescriptionList,
  forgetPassword,
  otp,
  profile,
  settings,
  settingsAi,
  selectedDrugs,
  favoriteDrugs,
  surplusDrugs,
  jobsList,
  pharmacyMessage,
  surveyList,
  aPharmacyDocs,
  fda_exchangeList,
  survey,
  error401,
  commisionSettingsList,
  employmentApplicationForAdmin,
  parcelsList,
  about,
  loginCountReport,
  allPharmacyDrugReport,
  acceptedPrescriptionsReport,
  allEmploymentApplicationsReport,
  allAccountingReport,
  allAccountingFullReport,
  allPharmacyWithSurplusDrugsReport,
  allSubscriptionReport,
  ostanRankingReport,
  allPacksReport,
  addSubscription,
  subscriptionList,
  pharmacySubscriptions,
  manageSubscriptions,
  declarationsList,
  pharmacyDrugHistoryReport,
  allFavoriteList,
  pharmacyProfile,
  addUserSubscription,
  userSubscriptions,
  userTransactions,
  allSubscriptionUserReport,
  manageUserSubscriptions,
  subscriptionUserList,
  addSubscriptionForUser,
  startQrcode,
  assignDrugCategory,
} = routes

const LoadingComponent: React.FC = () => {
  return (
    <>
      <Appbar showButtons={false} />
      <div style={{ marginTop: 75 }}>
        <CircleLoading />
      </div>
    </>
  )
}

const App = (): JSX.Element => {
  const history = createBrowserHistory()
  ReactGA.initialize('G-TKSLN0VE57')
  // history.listen((e): any => {
  //   const userInStorage = localStorage.getItem('user');
  //   const gaOptions: any = {};
  //   if (userInStorage) {
  //     const user = JSON.parse(userInStorage);
  //     gaOptions.user_name = user.name;
  //     gaOptions.user_family = user.family;
  //     gaOptions.pharmacyName = user.pharmacyName;
  //   }
  //   ReactGA.set({
  //     ...gaOptions,
  //     page: `${e.location.pathname}${e.location.search}${e.location.hash}`,
  //   }); // Update the user's current page
  //   // Record a pageview for the given page
  //   ReactGA.pageview(`${e.location.pathname}${e.location.search}${e.location.hash}`);
  // })

  /*
  try {
    const gaScript = document.createElement('script')
    gaScript.async = true
    gaScript.src = 'https://www.googletagmanager.com/gtag/js?id=UA-189684012-1'
    document.body.appendChild(gaScript)
    const gaScript2 = document.createElement('script')
    gaScript2.text =
      ' window.dataLayer = window.dataLayer || [];' +
      'function gtag(){dataLayer.push(arguments);}' +
      "gtag('js', new Date());" +
      "gtag('config', 'UA-189684012-1');"
    document.body.appendChild(gaScript2)
  } catch (gaError) {
    errorHandler(gaError)
  }
  */

  const setupCrisp = (): void => {
    // ;(window as any).$crisp = []
    ;(function (): void {
      const d = document
      const s = d.createElement('script')

      s.src = 'https://client.crisp.chat/l.js'
      s.async = true
      d.getElementsByTagName('head')[0].appendChild(s)
    })()
  }

  const setupGoftino = (): void => {
    ;(function (): void {
      const d = document
      const s = d.createElement('script')

      s.text =
        '!function(){var i="A5tPxl",a=window,d=document;function g(){' +
        'var g=d.createElement("script"),s="https://www.goftino.com/widget/"+i,' +
        'l=localStorage.getItem("goftino_"+i);g.async=!0,g.src=l?s+"?o="+l:s;' +
        'd.getElementsByTagName("head")[0].appendChild(g);' +
        // These are added to hide goftino in all pages but
        // /#/dashboard/exchange/transfer?&step=1
        'window.addEventListener("hashchange",function(event){' +
        'const goftino_w=document.getElementById("goftino_w");' +
        'goftino_w.style.display=' +
        'window.location.hash==="#' +
        defaultPath +
        '"' +
        '?"block":"none"})' +
        ///// till here
        '}"complete"===' +
        'd.readyState?g():a.attachEvent?a.attachEvent("onload",g):' +
        'a.addEventListener("load",g,!1);}();'

      d.getElementsByTagName('head')[0].appendChild(s)
    })()
  }

  // It checks if the user is just pharmacy or not
  // Or not means: pharmacy and admin, or public
  const isJustPharmacy = useCallback(() => {
    let rolesArray = new JwtData().rolesFromMainToken()
    const isPharmacy =
      rolesArray?.indexOf(RolesEnum.PHARMACY) >= 0 ||
      rolesArray?.some((item: any) =>
        GetValuesOfEnum(PharmacyRoleEnum).includes(item),
      )
    const isPharmacyAndNotAdmin =
      isPharmacy && rolesArray?.indexOf(RolesEnum.ADMIN) < 0
    return isPharmacyAndNotAdmin
  }, [])

  const { hasPharmacySubscribe } = new Subscription()
  const checkSubscription = async (): Promise<any> => {
    // If it's not just a pharmacy so it'll ba an admin or public
    // so we return true, therefore we won't check it's subscription

    const hasSubscription = await hasPharmacySubscribe()
    return hasSubscription
  }

  const { hasUserSubscribe } = new SubscriptionUser()
  const checkUserSubscription = async (): Promise<any> => {
    // If it's not just a pharmacy so it'll ba an admin or public
    // so we return true, therefore we won't check it's subscription

    const hasSubscription = await hasUserSubscribe()
    return hasSubscription
  }

  history.listen(
    async ({ action, location }): Promise<any> => {
      // No need to check subscription in these pages
      if (
        location.hash.includes('/login') ||
        location.hash.includes('error/401') ||
        !localStorage.getItem('mainToken')
      )
        return

      if (isJustPharmacy()) {
        const hasSubscription = await checkSubscription()
        if (
          // if it's === true, it means it's not just pharmacy,
          // so we don't check subscription
          !hasSubscription.data.hasSubscribe
        ) {
          tWarn(hasSubscription.message, { toastId: 'checkSubscription' })
          // Everyone allowed to subscription-list page, to see his/her subscriptions
          if (!window.location.hash.endsWith(subscriptionList)) {
            window.location.hash = addSubscription
          }
        }
      }
      await checkUserSubscription()
    },
  )

  //setupCrisp()
  const currentWidth = window.outerWidth
  if (currentWidth > 760) setupGoftino()
  return (
    <>
      <ToastContainer rtl={true} closeButton={false} position="bottom-right" />
      <Router>
        <Switch>
          <Suspense fallback={<LoadingComponent />}>
            <CssBaseline />
            <PublicRoute exact path={['/', login]}>
              <LoginNew />
            </PublicRoute>
            {/* <PublicRoute exact path={['/loginNew', loginNew]}>
              <LoginNew />
            </PublicRoute> */}
            <PublicRoute exact path={['/loginPharmacy', loginPharmacy]}>
              <LoginPharmacy />
            </PublicRoute>
            <PublicRoute
              exact
              path={['/forgetPasswordPharmacy', forgetPasswordPharmacy]}
            >
              <ForgetPasswordPharmacy />
            </PublicRoute>
            {/* <PublicRoute exact path={['/forgetPasswordNew', forgetPasswordNew]}>
              <ForgetPasswordNew />
            </PublicRoute> */}
            <PublicRoute exact path={['/otpPharmacy', otpPharmacy]}>
              <OtpPharmacy />
            </PublicRoute>
            {/* <PublicRoute exact path={['/otpNew', otpNew]}>
              <OtpNew />
            </PublicRoute> */}
            <PublicRoute exact path={forgetPassword}>
              <ForgetPasswordNew />
            </PublicRoute>
            <PublicRoute exact path={otp}>
              <OtpNew />
            </PublicRoute>
            <PublicRoute exact path={registerPharmacyWithUser}>
              <RegisterPharmacyWithUser />
            </PublicRoute>
            <PublicRoute path={startQrcode}></PublicRoute>

            <PrivateRoute exact path={defaultPath}>
              <Dashboard component={<Transfer />} />
            </PrivateRoute>
            <PrivateRoute exact path={dashboard}>
              <Dashboard component={<DashboardContent />} />
            </PrivateRoute>
            <PrivateRoute exact path="/dashboardActivePage">
              <Dashboard component={<DashboardActivePage />} />
            </PrivateRoute>
            <PrivateRoute exact path={desktop}>
              <Dashboard component={<Desktop1 />} />
            </PrivateRoute>
            <PrivateRoute exact path={transfer}>
              <Dashboard component={<Transfer />} />
            </PrivateRoute>
            <PrivateRoute exact path={survey}>
              <Dashboard component={<Survey />} />
            </PrivateRoute>
            <PrivateRoute exact path={supplyList}>
              <Dashboard component={<SupplyList />} />
            </PrivateRoute>
            <PrivateRoute exact path={suggestDrug}>
              <Dashboard component={<SuggestDrug />} />
            </PrivateRoute>
            <PrivateRoute exact path={transactions}>
              <Dashboard component={<Transactions />} />
            </PrivateRoute>
            <PrivateRoute exact path={membershipRequests}>
              <Dashboard component={<MembershipRequests />} />
            </PrivateRoute>
            <PrivateRoute exact path={memberRole}>
              <Dashboard component={<MemberRole />} />
            </PrivateRoute>
            <PrivateRoute exact path={createRole}>
              <Dashboard component={<CreateRole />} />
            </PrivateRoute>
            <PrivateRoute exact path={createUser}>
              <Dashboard component={<CreateUser />} />
            </PrivateRoute>
            <PrivateRoute exact path={usersList}>
              <Dashboard component={<UsersList />} />
            </PrivateRoute>
            <PrivateRoute exact path={changeUserPassword}>
              <Dashboard component={<ChangeUserPassword />} />
            </PrivateRoute>
            <PrivateRoute exact path={createDrug}>
              <Dashboard component={<CreateDrug />} />
            </PrivateRoute>
            <PrivateRoute exact path={drugsList} permission="drugs:alldrugs">
              <Dashboard component={<DrugsList />} />
            </PrivateRoute>
            <PrivateRoute exact path={categoryList}>
              <Dashboard component={<CategoryList />} />
            </PrivateRoute>
            <PrivateRoute exact path={createPharmacy}>
              <Dashboard component={<CreatePharmacy />} />
            </PrivateRoute>
            <PrivateRoute exact path={pharmaciesList}>
              <Dashboard component={<PharmaciesList />} />
            </PrivateRoute>
            <PrivateRoute exact path={pharmacyDocs}>
              <Dashboard component={<PharmacyDocs />} />
            </PrivateRoute>
            <PrivateRoute exact path={createMessage}>
              <Dashboard component={<CreateMessage />} />
            </PrivateRoute>
            <PrivateRoute exact path={messagesList}>
              <Dashboard component={<MessagesList />} />
            </PrivateRoute>
            <PrivateRoute exact path={reviewsList}>
              <Dashboard component={<ReviewsList />} />
            </PrivateRoute>
            <PrivateRoute exact path={drugFavoriteList}>
              <Dashboard component={<FavoriteList />} />
            </PrivateRoute>
            <PrivateRoute exact path={drugCategoryfavoriteList}>
              <Dashboard component={<DrugFavoriteCategory />} />
            </PrivateRoute>
            <PrivateRoute exact path={allFavoriteList}>
              <Dashboard component={<AllFavoritesForm />} />
            </PrivateRoute>
            <PrivateRoute exact path={accountingInfo}>
              <Dashboard component={<AccountingInfo />} />
            </PrivateRoute>

            <PrivateRoute exact path={addSubscription}>
              <Dashboard component={<AddSubscription />} />
            </PrivateRoute>
            <PrivateRoute exact path={addSubscriptionForUser}>
              <Dashboard component={<AddSubscriptionForUser />} />
            </PrivateRoute>
            <PrivateRoute exact path={addUserSubscription}>
              <Dashboard component={<AddUserSubscription />} />
            </PrivateRoute>
            <PrivateRoute exact path={subscriptionList}>
              <Dashboard component={<SubscriptionList />} />
            </PrivateRoute>
            <PrivateRoute exact path={subscriptionUserList}>
              <Dashboard component={<SubscriptionUserList />} />
            </PrivateRoute>
            <PrivateRoute exact path={manageSubscriptions}>
              <Dashboard component={<ManageSubscriptions />} />
            </PrivateRoute>
            <PrivateRoute exact path={manageUserSubscriptions}>
              <Dashboard component={<ManageUserSubscriptions />} />
            </PrivateRoute>
            <PrivateRoute exact path={packsList}>
              <Dashboard component={<PackList />} />
            </PrivateRoute>
            <PrivateRoute exact path={prescription}>
              <Dashboard component={<Prescription />} />
            </PrivateRoute>
            <PrivateRoute exact path={jobApplication}>
              <Dashboard component={<EmploymentApplication />} />
            </PrivateRoute>
            <PrivateRoute exact path={findJob}>
              <Dashboard component={<FindJob />} />
            </PrivateRoute>
            <PrivateRoute exact path={addTransaction}>
              <Dashboard component={<AddTransaction />} />
            </PrivateRoute>
            <PrivateRoute exact path={drugCategoryfavoriteList}>
              <Dashboard component={<DrugFavoriteCategory />} />
            </PrivateRoute>
            <PrivateRoute exact path={createPack}>
              <Dashboard component={<CreatePack />} />
            </PrivateRoute>
            <PrivateRoute exact path={`${createPack}/:packId`}>
              <Dashboard component={<CreatePack />} />
            </PrivateRoute>
            <PrivateRoute exact path={exchangeManagementList}>
              <Dashboard component={<ExchangeManagement />} />
            </PrivateRoute>
            <PrivateRoute exact path={pharmacyUsersList}>
              <Dashboard component={<PharmacyUsersList />} />
            </PrivateRoute>
            <PrivateRoute exact path={jobSearchList}>
              <Dashboard component={<JobSearchList full={isAdmin()} />} />
            </PrivateRoute>
            <PrivateRoute exact path={prescriptionList}>
              <Dashboard component={<PrescriptionList />} />
            </PrivateRoute>
            {/*<Route component={<>404 Not Found</>} />*/}
            <PrivateRoute exact path={profile}>
              <Dashboard component={<ProfileLazy />} />
            </PrivateRoute>
            <PrivateRoute exact path={pharmacyProfile}>
              <Dashboard component={<PharmacyProfileLazy />} />
            </PrivateRoute>
            <PrivateRoute exact path={settings}>
              <Dashboard component={<SettingsForm />} />
            </PrivateRoute>
            <PrivateRoute exact path={settingsAi}>
              <Dashboard component={<SettingsAiForm />} />
            </PrivateRoute>
            <PrivateRoute exact path={surplusDrugs}>
              <Dashboard component={<SurplusDrugsForm />} />
            </PrivateRoute>
            <PrivateRoute exact path={favoriteDrugs}>
              <Dashboard component={<FavoriteDrugsForm />} />
            </PrivateRoute>
            <PrivateRoute exact path={selectedDrugs}>
              <Dashboard component={<SelectedDrugsForm />} />
            </PrivateRoute>
            <PrivateRoute exact path={loginCountReport}>
              <Dashboard component={<LoginCountReport />} />
            </PrivateRoute>
            <PrivateRoute exact path={allPharmacyDrugReport}>
              <Dashboard component={<AllPharmacyDrugReport />} />
            </PrivateRoute>
            <PrivateRoute exact path={acceptedPrescriptionsReport}>
              <Dashboard component={<AcceptedPrescriptionsReport />} />
            </PrivateRoute>
            <PrivateRoute exact path={allEmploymentApplicationsReport}>
              <Dashboard component={<AllEmploymentApplicationsReport />} />
            </PrivateRoute>
            <PrivateRoute exact path={pharmacySubscriptions}>
              <Dashboard component={<PharmacySubscriptions />} />
            </PrivateRoute>
            <PrivateRoute exact path={userSubscriptions}>
              <Dashboard component={<UserSubscriptions />} />
            </PrivateRoute>
            <PrivateRoute exact path={userTransactions}>
              <Dashboard component={<UserTransactions />} />
            </PrivateRoute>
            <PrivateRoute exact path={allAccountingReport}>
              <Dashboard component={<AllAccountingReport />} />
            </PrivateRoute>
            <PrivateRoute exact path={allAccountingFullReport}>
              <Dashboard component={<AllAccountingFullReport />} />
            </PrivateRoute>
            <PrivateRoute exact path={allPharmacyWithSurplusDrugsReport}>
              <Dashboard component={<AllPharmacyWithSurplusDrugsReport />} />
            </PrivateRoute>
            <PrivateRoute exact path={assignDrugCategory} permission="drugs:alldrugs">
              <Dashboard component={<GroupDrugCategory />} />
            </PrivateRoute>
            <PrivateRoute exact path={allSubscriptionReport}>
              <Dashboard component={<AllSubscriptionReport />} />
            </PrivateRoute>
            <PrivateRoute exact path={allSubscriptionUserReport}>
              <Dashboard component={<AllSubscriptionUserReport />} />
            </PrivateRoute>
            <PrivateRoute exact path={ostanRankingReport}>
              <Dashboard component={<OstanRankingReport />} />
            </PrivateRoute>
            <PrivateRoute exact path={allPacksReport}>
              <Dashboard component={<AllPacksReport />} />
            </PrivateRoute>

            <PrivateRoute path={jobsList}>
              <Dashboard component={<Job />} />
            </PrivateRoute>
            <PrivateRoute exact path={pharmacyMessage}>
              <Dashboard component={<PharmacyMessage />} />
            </PrivateRoute>
            <PrivateRoute path={surveyList}>
              <Dashboard component={<SurveyComponent />} />
            </PrivateRoute>
            <PrivateRoute path={aPharmacyDocs}>
              <Dashboard component={<APharmacyDocs />} />
            </PrivateRoute>
            <PrivateRoute path={commisionSettingsList}>
              <Dashboard component={<CommisionsList />} />
            </PrivateRoute>
            <PrivateRoute exact path={fda_exchangeList}>
              <Dashboard component={<FDA_exchangeList />} />
            </PrivateRoute>
            <PrivateRoute exact path={employmentApplicationForAdmin}>
              <Dashboard component={<EmploymentApplicationForAdmin />} />
            </PrivateRoute>
            <PrivateRoute exact path={parcelsList} >
              <Dashboard component={<ParcelList />} />
            </PrivateRoute>
            <PrivateRoute exact path={declarationsList}>
              <Dashboard component={<DeclarationsList />} />
            </PrivateRoute>
            <PrivateRoute exact path={pharmacyDrugHistoryReport}>
              <Dashboard component={<PharmacyDrugHistoryReport />} />
            </PrivateRoute>

            <PrivateRoute exact path={error401}>
              <Error401 />
            </PrivateRoute>

            <PrivateRoute exact path={about}>
              <Dashboard component={<About />} />
            </PrivateRoute>

            <Route
              render={(e): any => {
                console.log(e.location.pathname)
                if (e.location.pathname === '/') return
                if (
                  !Object.values(routes).some((i) =>
                    e.location.pathname.startsWith(i),
                  )
                ) {
                  return <Error404 />
                }
              }}
            />
          </Suspense>
        </Switch>
      </Router>
      <ReactQueryDevtools />
    </>
  )
}

export default App
